import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon, Plus, Bell, Clock, User, Loader2, CheckCircle,
  ChevronLeft, ChevronRight, Trash2, Phone, MapPin, Briefcase, ExternalLink,
} from 'lucide-react';
import { reminderAPI, jobAPI, customerAPI, userAPI } from '../api/client';
import apiClient from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// 6am → 10pm, hourly buckets
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);

const STATUS_COLOR = {
  pending: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-200',
  dispatched: 'bg-orange-500/15 border-orange-500/40 text-orange-200',
  assigned: 'bg-sky-500/15 border-sky-500/40 text-sky-200',
  scheduled: 'bg-blue-500/15 border-blue-500/40 text-blue-200',
  in_progress: 'bg-purple-500/15 border-purple-500/40 text-purple-200',
  completed: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200',
  invoiced: 'bg-teal-500/15 border-teal-500/40 text-teal-200',
  paid: 'bg-green-500/15 border-green-500/40 text-green-200',
  cancelled: 'bg-rose-500/15 border-rose-500/40 text-rose-200',
};

export default function Schedule() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());

  // Book Job dialog
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDateTime, setBookingDateTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookForm, setBookForm] = useState({
    // Customer
    existing_customer_id: '',
    first_name: '', last_name: '', phone: '', email: '', address: '',
    // Job
    job_title: '', job_service_type: '', job_priority: 'normal',
    job_labor_cost: '', technician_id: '',
  });

  // Reminder dialog (kept for callbacks)
  const [remDialogOpen, setRemDialogOpen] = useState(false);
  const [remForm, setRemForm] = useState({ title: '', type: 'callback', customer_id: '', remind_date: '', notes: '' });
  const [remSubmitting, setRemSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [r, j, c, u] = await Promise.all([
        reminderAPI.list().catch(() => []),
        jobAPI.list().catch(() => []),
        customerAPI.list().catch(() => []),
        userAPI.list().catch(() => []),
      ]);
      setReminders(r); setJobs(j); setCustomers(c);
      setTechnicians((u || []).filter((x) => x.role === 'technician' || x.role === 'company_admin'));
    } catch (e) { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  };

  // Calendar grid
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calendar = [];
  for (let i = 0; i < firstDay; i++) calendar.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendar.push(new Date(year, month, d));

  const isToday = (d) => d && d.toDateString() === new Date().toDateString();
  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

  const customerById = (id) => customers.find((c) => c.id === id);
  const techById = (id) => technicians.find((t) => t.id === id);

  // Jobs scheduled on a specific date
  const jobsOn = (date) => {
    if (!date) return [];
    const ds = date.toDateString();
    return jobs.filter((j) => j.scheduled_date && new Date(j.scheduled_date).toDateString() === ds);
  };

  const remindersOn = (date) => {
    if (!date) return [];
    const ds = date.toDateString();
    return reminders.filter((r) => !r.is_completed && new Date(r.remind_date).toDateString() === ds);
  };

  // Jobs in a specific 1-hr bucket on a date
  const jobsInHour = (date, hour) => {
    return jobsOn(date).filter((j) => new Date(j.scheduled_date).getHours() === hour);
  };

  // Open Book Job dialog with a pre-filled datetime
  const openBookingAt = (date, hour) => {
    const dt = new Date(date);
    dt.setHours(hour, 0, 0, 0);
    const localIso = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setBookingDateTime(localIso);
    setBookForm({
      existing_customer_id: '',
      first_name: '', last_name: '', phone: '', email: '', address: '',
      job_title: '', job_service_type: '', job_priority: 'normal',
      job_labor_cost: '', technician_id: '',
    });
    setBookingOpen(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!bookForm.job_title && !bookForm.job_service_type) return toast.error('Add a job title or service type');
    const scheduled = new Date(bookingDateTime).toISOString();
    setSubmitting(true);
    try {
      let customerId = bookForm.existing_customer_id;
      // 1. Create customer if not existing
      if (!customerId) {
        if (!bookForm.first_name || !bookForm.phone) return toast.error('Customer name + phone required');
        const newCust = await customerAPI.create({
          first_name: bookForm.first_name,
          last_name: bookForm.last_name,
          phone: bookForm.phone,
          email: bookForm.email || null,
          address: bookForm.address || null,
        });
        customerId = newCust.id;
      }
      // 2. Create the job
      const created = await jobAPI.create({
        customer_id: customerId,
        title: bookForm.job_title || bookForm.job_service_type || 'Scheduled Job',
        service_type: bookForm.job_service_type || null,
        priority: bookForm.job_priority || 'normal',
        scheduled_date: scheduled,
        labor_cost: bookForm.job_labor_cost ? parseFloat(bookForm.job_labor_cost) : 0,
      });
      // 3. Assign tech if picked
      if (bookForm.technician_id) {
        await jobAPI.update(created.id, { technician_id: bookForm.technician_id, status: 'assigned' });
      } else {
        await jobAPI.update(created.id, { status: 'scheduled' });
      }
      toast.success('Job booked and added to schedule');
      setBookingOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not book job');
    } finally { setSubmitting(false); }
  };

  const submitReminder = async (e) => {
    e.preventDefault();
    if (!remForm.title || !remForm.remind_date) return toast.error('Title + date required');
    setRemSubmitting(true);
    try {
      const customer = customers.find((c) => c.id === remForm.customer_id);
      await reminderAPI.create({
        ...remForm,
        remind_date: new Date(remForm.remind_date).toISOString(),
        customer_name: customer ? `${customer.first_name} ${customer.last_name}` : null,
        customer_phone: customer?.phone || null,
      });
      toast.success('Reminder created');
      setRemDialogOpen(false);
      setRemForm({ title: '', type: 'callback', customer_id: '', remind_date: '', notes: '' });
      await loadData();
    } catch { toast.error('Failed'); }
    finally { setRemSubmitting(false); }
  };

  const completeReminder = async (id) => {
    try { await reminderAPI.update(id, { is_completed: true }); toast.success('Done'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const deleteReminder = async (id) => {
    if (!window.confirm('Delete reminder?')) return;
    try { await reminderAPI.delete(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const goToCustomer = (customerId, jobId) => {
    // Navigate to customers page focused on this customer (via query param)
    navigate(`/customers?focus=${customerId}${jobId ? `&job=${jobId}` : ''}`);
  };

  const goToJob = (jobId) => navigate(`/jobs?focus=${jobId}`);

  if (loading) {
    return <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></CardContent></Card>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-primary" /> Schedule
          </h1>
          <p className="text-muted-foreground">All techs · click a time to book · click a job to see the customer</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setRemDialogOpen(true)} data-testid="add-reminder-button">
            <Bell className="w-4 h-4 mr-1" /> Reminder
          </Button>
          <Button onClick={() => openBookingAt(selectedDay, new Date().getHours())} className="btn-gradient-primary" data-testid="book-job-button">
            <Plus className="w-4 h-4 mr-1" /> Book Job
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Month grid */}
        <Card className="lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <Button size="sm" variant="outline" onClick={() => setCurrentMonth(new Date(year, month - 1))}><ChevronLeft className="w-4 h-4" /></Button>
              <h2 className="font-bold">{MONTHS[month]} {year}</h2>
              <Button size="sm" variant="outline" onClick={() => setCurrentMonth(new Date(year, month + 1))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendar.map((d, i) => {
                const dayJobs = jobsOn(d);
                const dayRems = remindersOn(d);
                const totalCount = dayJobs.length + dayRems.length;
                return (
                  <button
                    key={i}
                    onClick={() => d && setSelectedDay(d)}
                    disabled={!d}
                    className={`aspect-square p-1 rounded text-sm transition-all relative ${
                      !d ? '' :
                      isSameDay(d, selectedDay) ? 'bg-primary text-primary-foreground ring-2 ring-primary' :
                      isToday(d) ? 'bg-primary/20 text-primary font-bold' :
                      'hover:bg-muted'
                    }`}
                    data-testid={d ? `calendar-day-${d.toISOString().slice(0, 10)}` : undefined}
                  >
                    {d && (
                      <>
                        <div>{d.getDate()}</div>
                        {totalCount > 0 && (
                          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dayJobs.length > 0 && <div className="w-1 h-1 rounded-full bg-blue-500" />}
                            {dayRems.length > 0 && <div className="w-1 h-1 rounded-full bg-orange-500" />}
                          </div>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" /> Jobs</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-orange-500" /> Reminders</div>
            </div>
          </CardContent>
        </Card>

        {/* Day timeline (across all techs) */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              <Badge className="bg-sky-500/15 text-sky-300 border border-sky-500/40">
                {jobsOn(selectedDay).length} jobs · {remindersOn(selectedDay).length} reminders
              </Badge>
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1" data-testid="day-timeline">
              {HOURS.map((h) => {
                const hourJobs = jobsInHour(selectedDay, h);
                const label = new Date(2000, 0, 1, h).toLocaleTimeString('en-US', { hour: 'numeric' });
                return (
                  <div key={h} className="flex gap-3 items-start group">
                    <div className="w-16 pt-1 text-xs text-muted-foreground text-right shrink-0">{label}</div>
                    <div className="flex-1 min-h-[44px] border-l pl-3 space-y-1.5">
                      {hourJobs.length === 0 ? (
                        <button
                          onClick={() => openBookingAt(selectedDay, h)}
                          className="text-xs text-muted-foreground hover:text-sky-400 transition opacity-0 group-hover:opacity-100"
                          data-testid={`book-slot-${h}`}
                        >
                          + book a customer at {label}
                        </button>
                      ) : (
                        hourJobs.map((j) => {
                          const cust = customerById(j.customer_id);
                          const tech = techById(j.technician_id);
                          const cls = STATUS_COLOR[j.status] || 'bg-muted border';
                          return (
                            <div
                              key={j.id}
                              className={`rounded-lg border p-2.5 ${cls} hover:shadow cursor-pointer transition`}
                              onClick={() => goToCustomer(j.customer_id, j.id)}
                              data-testid={`job-slot-${j.id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-sm flex items-center gap-1">
                                    <Briefcase className="w-3 h-3 shrink-0" /> {j.title}
                                  </p>
                                  {cust && (
                                    <p className="text-xs mt-1 flex items-center gap-1">
                                      <User className="w-3 h-3" /> {cust.first_name} {cust.last_name}
                                    </p>
                                  )}
                                  {cust?.phone && (
                                    <p className="text-xs flex items-center gap-1">
                                      <Phone className="w-3 h-3" /> {cust.phone}
                                    </p>
                                  )}
                                  {cust?.address && (
                                    <p className="text-xs flex items-center gap-1 truncate">
                                      <MapPin className="w-3 h-3 shrink-0" /> {cust.address}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[10px] uppercase tracking-wider opacity-80">{j.status?.replace('_', ' ')}</p>
                                  {tech ? (
                                    <p className="text-[11px] mt-1 flex items-center gap-1 justify-end">
                                      {tech.avatar_url ? <img src={tech.avatar_url} alt="" className="w-4 h-4 rounded-full object-cover" /> : null}
                                      {tech.first_name}
                                    </p>
                                  ) : (
                                    <p className="text-[11px] mt-1 opacity-60">unassigned</p>
                                  )}
                                  <ExternalLink className="w-3 h-3 inline mt-1 opacity-60" />
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Day reminders below the timeline */}
            {remindersOn(selectedDay).length > 0 && (
              <div className="mt-4 pt-3 border-t space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reminders today</p>
                {remindersOn(selectedDay).map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold flex items-center gap-1"><Bell className="w-3 h-3" /> {r.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.remind_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {r.customer_name ? ` · ${r.customer_name}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => completeReminder(r.id)} className="h-7 w-7 p-0"><CheckCircle className="w-3 h-3 text-green-500" /></Button>
                      <Button size="sm" variant="outline" onClick={() => deleteReminder(r.id)} className="h-7 w-7 p-0"><Trash2 className="w-3 h-3 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Book Job dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="book-job-dialog">
          <DialogHeader>
            <DialogTitle>Book a Job</DialogTitle>
            <DialogDescription>
              {new Date(bookingDateTime || Date.now()).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              {' · '}Coordinates with Dispatch + Pipeline automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer</p>
              <Select value={bookForm.existing_customer_id} onValueChange={(v) => setBookForm({ ...bookForm, existing_customer_id: v })}>
                <SelectTrigger data-testid="booking-existing-customer"><SelectValue placeholder="Pick existing customer or fill new below" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.phone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!bookForm.existing_customer_id && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="First name" value={bookForm.first_name} onChange={(e) => setBookForm({ ...bookForm, first_name: e.target.value })} data-testid="booking-first-name" />
                  <Input placeholder="Last name" value={bookForm.last_name} onChange={(e) => setBookForm({ ...bookForm, last_name: e.target.value })} />
                  <Input placeholder="Phone *" value={bookForm.phone} onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })} data-testid="booking-phone" />
                  <Input placeholder="Email" type="email" value={bookForm.email} onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })} />
                  <Input placeholder="Address" value={bookForm.address} onChange={(e) => setBookForm({ ...bookForm, address: e.target.value })} className="col-span-2" />
                </div>
              )}
            </div>

            <div className="rounded-lg border bg-cyan-500/5 border-cyan-500/30 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-cyan-300">Job details</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Job title *" value={bookForm.job_title} onChange={(e) => setBookForm({ ...bookForm, job_title: e.target.value })} data-testid="booking-job-title" />
                <Input placeholder="Service type (e.g. Car Lockout)" value={bookForm.job_service_type} onChange={(e) => setBookForm({ ...bookForm, job_service_type: e.target.value })} />
                <Select value={bookForm.job_priority} onValueChange={(v) => setBookForm({ ...bookForm, job_priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Labor estimate ($)" type="number" step="0.01" value={bookForm.job_labor_cost} onChange={(e) => setBookForm({ ...bookForm, job_labor_cost: e.target.value })} />
                <Select value={bookForm.technician_id} onValueChange={(v) => setBookForm({ ...bookForm, technician_id: v })}>
                  <SelectTrigger className="col-span-2" data-testid="booking-tech"><SelectValue placeholder="Assign tech (optional)" /></SelectTrigger>
                  <SelectContent>
                    {technicians.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name} · {t.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="datetime-local" value={bookingDateTime} onChange={(e) => setBookingDateTime(e.target.value)} className="col-span-2" />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBookingOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="btn-gradient-primary" data-testid="booking-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Book & schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reminder dialog */}
      <Dialog open={remDialogOpen} onOpenChange={setRemDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Reminder</DialogTitle><DialogDescription>Schedule a callback or follow-up</DialogDescription></DialogHeader>
          <form onSubmit={submitReminder} className="space-y-3">
            <div className="space-y-2"><Label>Title *</Label><Input value={remForm.title} onChange={(e) => setRemForm({ ...remForm, title: e.target.value })} placeholder="e.g., Call John back" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={remForm.type} onValueChange={(v) => setRemForm({ ...remForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="callback">Callback</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="followup">Follow-up</SelectItem>
                    <SelectItem value="custom">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Remind At *</Label>
                <Input type="datetime-local" value={remForm.remind_date} onChange={(e) => setRemForm({ ...remForm, remind_date: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2"><Label>Customer (optional)</Label>
              <Select value={remForm.customer_id} onValueChange={(v) => setRemForm({ ...remForm, customer_id: v })}>
                <SelectTrigger><SelectValue placeholder="Link a customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={remForm.notes} onChange={(e) => setRemForm({ ...remForm, notes: e.target.value })} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRemDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={remSubmitting}>{remSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
